import React, { useState, useMemo, useCallback } from 'react';
import { Form } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { message } from 'antd';
import { useCanCheck } from '@/ability/helpers';
import { useAuthStore } from '@stores/auth.store';
import StatusBadge from '@/components/ui/StatusBadge';
import { getLeaves, getLeaveById, createLeave, getLeaveTypes, manageLeaveTypes } from '../services/leave.service';
import { LEAVES_QUERY_KEY, LEAVE_DETAIL_QUERY_KEY } from './leavesQueryKeys';
import { getLeaveColumns } from '../columns/leaveColumns';

const STATUS_TAB = { ALL: 'all', PENDING: 'pending', APPROVED: 'approved', REJECTED: 'rejected' };
export const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');

/** Format date as "18th March 2026" for display. */
const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return dateStr;
        return format(d, 'do MMMM yyyy');
    } catch {
        return dateStr;
    }
};

/** Format date and time as "9th March 2026, 07:52" for display. */
const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return dateStr;
        return format(d, 'do MMMM yyyy, HH:mm');
    } catch {
        return dateStr;
    }
};

export const useLeaveDetail = (leaveId) =>
    useQuery({
        queryKey: LEAVE_DETAIL_QUERY_KEY(leaveId),
        queryFn: () => getLeaveById(leaveId),
        enabled: !!leaveId,
    });

export const useUpdateLeaveStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, status, remarks, startDate, endDate }) => 
            updateLeaveStatus(id, { status, remarks, startDate, endDate }),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: LEAVES_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: LEAVE_DETAIL_QUERY_KEY(id) });
        },
    });
};

export const useLeaveDetailPanel = (leaveId) => {
    const can = useCanCheck();
    const canView = can('hr.leave.view') || can('hr.leaves.view');
    const canUpdate = can('hr.leave.update') || can('hr.leaves.update');
    const { data: leave, isLoading, isError } = useLeaveDetail(leaveId);
    const isPending = leave && (leave.status || '').toLowerCase() === 'pending';

    return {
        leave,
        isLoading,
        isError,
        canUpdate,
        canView,
        isPending,
        formatDate,
        formatDateTime,
        capFirst,
    };
};

export const LEAVE_TYPES = [
    { value: 'annual', label: 'Annual' },
    { value: 'sick', label: 'Sick' },
    { value: 'other', label: 'Other' },
];

export const useLeaves = () => {
    const queryClient = useQueryClient();
    const [createForm] = Form.useForm();
    const [searchText, setSearchText] = useState('');
    const [statusTab, setStatusTab] = useState(STATUS_TAB.ALL);
    const [filterStart, setFilterStart] = useState(null);
    const [filterEnd, setFilterEnd] = useState(null);
    const [filterType, setFilterType] = useState(undefined);
    const [filterStatus, setFilterStatus] = useState(undefined);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);
    const [selectedLeaveId, setSelectedLeaveId] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    /** null | 'approve' | 'reject' — AppModal confirm for leave status (not SidePanel). */
    const [leaveStatusConfirm, setLeaveStatusConfirm] = useState(null);

    const updateLeaveStatusMutation = useUpdateLeaveStatus();

    const can = useCanCheck();
    const role = useAuthStore((s) => s.role);
    const user = useAuthStore((s) => s.user);
    const isCompanyAdmin = (role?.name ?? role) === 'Company Admin';
    const canView = can('hr.leave.view') || can('hr.leaves.view');
    const canCreate = (can('hr.leave.request')) && !isCompanyAdmin;
    const canUpdate = can('hr.leave.update') || can('hr.leaves.update');
    const canManage = can('hr.leave.manage');
    const canApprove = can('hr.leave.approve');

    const [searchParams, setSearchParams] = useSearchParams();
    const viewParam = searchParams.get('view');
    const defaultView = canCreate ? 'my' : 'employee';
    const activeView = viewParam === 'my' || viewParam === 'employee' ? viewParam : defaultView;
    const setActiveView = useCallback((val) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('view', val);
            return next;
        });
    }, [setSearchParams]);

    const { data: leaveTypes = [], refetch: refetchLeaveTypes } = useQuery({
        queryKey: ['leaveTypes'],
        queryFn: getLeaveTypes,
        enabled: !!canView || !!canManage,
    });

    const leaveTypeOptions = useMemo(() => {
        if (!leaveTypes || leaveTypes.length === 0) {
            return [
                { value: 'annual', label: 'Annual' },
                { value: 'sick', label: 'Sick' },
            ];
        }
        return leaveTypes.map(t => ({
            value: t.name.toLowerCase(),
            label: capFirst(t.name),
        }));
    }, [leaveTypes]);

    const manageLeaveTypesMutation = useMutation({
        mutationFn: (body) => manageLeaveTypes(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['leaveTypes'] });
            queryClient.invalidateQueries({ queryKey: LEAVES_QUERY_KEY });
            message.success('Leave configurations updated');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to update leave configurations');
        }
    });

    const createLeaveMutation = useMutation({
        mutationFn: (body) => createLeave(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: LEAVES_QUERY_KEY });
        },
    });

    const handleCreateLeave = useCallback(
        (values, callbacks = {}) => {
            const startDate = values.startDate ? dayjs(values.startDate).format('YYYY-MM-DD') : null;
            const endDate = values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null;
            if (!startDate || !endDate) {
                message.error('Please select start and end dates');
                return;
            }
            createLeaveMutation.mutate(
                { startDate, endDate, type: values.type || 'annual' },
                {
                    onSuccess: () => {
                        message.success('Leave request created');
                        setCreateModalOpen(false);
                        callbacks.onSuccess?.();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to create leave');
                        callbacks.onError?.(err);
                    },
                },
            );
        },
        [createLeaveMutation],
    );

    const { data: response, isError: leavesError } = useQuery({
        queryKey: [...LEAVES_QUERY_KEY, { page: 1, limit: 100, userId: activeView === 'my' ? user?.id : undefined }],
        queryFn: () => getLeaves({ page: 1, limit: 100, userId: activeView === 'my' ? user?.id : undefined }),
        enabled: !!canView,
        retry: false,
        staleTime: 0,
        refetchOnMount: true,
    });

    const leaves = response?.data ?? [];
    const total = response?.total ?? 0;
    const annualBalance = response?.annualBalance ?? 0;
    const leaveBalances = response?.leaveBalances ?? [];
    const pendingCount = useMemo(
        () => leaves.filter((l) => (l.status || '').toLowerCase() === 'pending').length,
        [leaves],
    );
    const approvedCount = useMemo(
        () => leaves.filter((l) => (l.status || '').toLowerCase() === 'approved').length,
        [leaves],
    );
    const rejectedCount = useMemo(
        () => leaves.filter((l) => (l.status || '').toLowerCase() === 'rejected').length,
        [leaves],
    );

    const selectedLeaveTitle = useMemo(() => {
        const leave = leaves.find((l) => l.id === selectedLeaveId);
        if (!leave) return 'Leave details';
        return `${leave.userName ?? 'Leave'} — ${capFirst(leave.type ?? '')} (${formatDate(leave.startDate)} - ${formatDate(leave.endDate)})`;
    }, [leaves, selectedLeaveId]);

    const selectedLeaveEmployeeName = useMemo(() => {
        const leave = leaves.find((l) => l.id === selectedLeaveId);
        return leave?.userName ?? 'Leave details';
    }, [leaves, selectedLeaveId]);

    const leaveRecordForStatusConfirm = useMemo(
        () => (selectedLeaveId ? leaves.find((l) => l.id === selectedLeaveId) ?? null : null),
        [leaves, selectedLeaveId],
    );

    const filterStatusOptions = useMemo(
        () => [
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
        ],
        [],
    );

    const activeFilterCount = [filterStart, filterEnd, filterType, filterStatus].filter(Boolean).length;

    const clearFilters = useCallback(() => {
        setFilterStart(null);
        setFilterEnd(null);
        setFilterType(undefined);
        setFilterStatus(undefined);
    }, []);

    const filteredLeaves = useMemo(() => {
        let list = leaves;
        if (statusTab !== STATUS_TAB.ALL) {
            list = list.filter((l) => (l.status || '').toLowerCase() === statusTab);
        }
        if (filterStart) {
            const s = filterStart.format('YYYY-MM-DD');
            list = list.filter((l) => (l.startDate || '') >= s);
        }
        if (filterEnd) {
            const e = filterEnd.format('YYYY-MM-DD');
            list = list.filter((l) => (l.endDate || '') <= e);
        }
        if (filterType) list = list.filter((l) => (l.type || '').toLowerCase() === filterType);
        if (filterStatus) list = list.filter((l) => (l.status || '').toLowerCase() === filterStatus);
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter(
                (l) =>
                    (l.userName || '').toLowerCase().includes(q) ||
                    (l.userEmail || '').toLowerCase().includes(q) ||
                    (l.type || '').toLowerCase().includes(q),
            );
        }
        return list;
    }, [leaves, statusTab, filterStart, filterEnd, filterType, filterStatus, searchText]);

    const tableData = useMemo(
        () =>
            filteredLeaves.map((l) => ({
                key: l.id,
                id: l.id,
                userName: l.userName,
                userEmail: l.userEmail,
                startDate: l.startDate,
                endDate: l.endDate,
                type: l.type,
                status: l.status,
                reason: l.reason,
                createdAt: l.createdAt,
            })),
        [filteredLeaves],
    );

    const openPanel = useCallback((id) => {
        setSelectedLeaveId(id);
        setPanelOpen(true);
    }, []);

    const columns = useMemo(
        () => getLeaveColumns({ openPanel }),
        [openPanel],
    );



    const closePanel = useCallback(() => {
        setPanelOpen(false);
        setSelectedLeaveId(null);
        setLeaveStatusConfirm(null);
    }, []);

    const openLeaveApproveConfirm = useCallback(() => {
        if (!selectedLeaveId) return;
        setLeaveStatusConfirm('approve');
    }, [selectedLeaveId]);

    const openLeaveRejectConfirm = useCallback(() => {
        if (!selectedLeaveId) return;
        setLeaveStatusConfirm('reject');
    }, [selectedLeaveId]);

    const closeLeaveStatusConfirm = useCallback(() => {
        setLeaveStatusConfirm(null);
    }, []);

    const confirmLeaveStatusAction = useCallback((values = {}) => {
        if (!canUpdate || !selectedLeaveId || !leaveStatusConfirm) return;
        const status = leaveStatusConfirm === 'approve' ? 'approved' : 'rejected';
        const payload = {
            id: selectedLeaveId,
            status,
            remarks: values.remarks || undefined,
            startDate: values.startDate ? (dayjs.isDayjs(values.startDate) ? values.startDate.format('YYYY-MM-DD') : values.startDate) : undefined,
            endDate: values.endDate ? (dayjs.isDayjs(values.endDate) ? values.endDate.format('YYYY-MM-DD') : values.endDate) : undefined,
        };
        updateLeaveStatusMutation.mutate(
            payload,
            {
                onSuccess: () => {
                    message.success(status === 'approved' ? 'Leave approved' : 'Leave rejected');
                    setLeaveStatusConfirm(null);
                    setPanelOpen(false);
                    setSelectedLeaveId(null);
                },
                onError: (err) => {
                    message.error(
                        err?.response?.data?.message ||
                            (leaveStatusConfirm === 'approve' ? 'Failed to approve' : 'Failed to reject'),
                    );
                },
            },
        );
    }, [canUpdate, selectedLeaveId, leaveStatusConfirm, updateLeaveStatusMutation]);

    const onSubmitCreate = useCallback(() => {
        createForm.validateFields().then((values) => {
            handleCreateLeave(values, { onSuccess: () => createForm.resetFields() });
        });
    }, [createForm, handleCreateLeave]);

    const closeCreateModal = useCallback(() => {
        setCreateModalOpen(false);
        createForm.resetFields();
    }, [createForm]);

    return {
        leaves,
        total,
        annualBalance,
        leaveBalances,
        canManage,
        canApprove,
        activeView,
        setActiveView,
        user,
        leaveTypes,
        leaveTypeOptions,
        manageLeaveTypes: manageLeaveTypesMutation.mutate,
        manageLeaveTypesPending: manageLeaveTypesMutation.isPending,
        refetchLeaveTypes,
        pendingCount,
        approvedCount,
        rejectedCount,
        filteredLeaves,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        // panel
        panelOpen,
        setPanelOpen,
        selectedLeaveId,
        setSelectedLeaveId,
        selectedLeaveTitle,
        selectedLeaveEmployeeName,
        openPanel,
        closePanel,
        leaveStatusConfirm,
        leaveRecordForStatusConfirm,
        openLeaveApproveConfirm,
        openLeaveRejectConfirm,
        closeLeaveStatusConfirm,
        confirmLeaveStatusAction,
        leaveStatusConfirmPending: updateLeaveStatusMutation.isPending,
        createModalOpen,
        setCreateModalOpen,
        createForm,
        onSubmitCreate,
        closeCreateModal,
        handleCreateLeave,
        createLeavePending: createLeaveMutation.isPending,
        canView,
        canCreate,
        canUpdate,
        leavesError,
        capFirst,
        formatDate,
        filterStart,
        setFilterStart,
        filterEnd,
        setFilterEnd,
        filterType,
        setFilterType,
        filterStatus,
        setFilterStatus,
        filterModalOpen,
        setFilterModalOpen,
        filterStatusOptions,
        activeFilterCount,
        clearFilters,
    };
};
